import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import PageBanner from "../../components/PageBanner";
import "./nispNew.css";
import { getNispPolicy } from '../../services/data.service';
import { mediaUrl } from '../../services/assetUrl';

const EASE = [0.22, 1, 0.36, 1];

const FrameworkIcon = ({ type }) => {
  const icons = {
    objectives: (
      <>
        <circle cx="12" cy="12" r="9" />
        <circle cx="12" cy="12" r="3" />
        <path d="M12 3v3M12 18v3M3 12h3M18 12h3" />
      </>
    ),
    goals: (
      <>
        <path d="M4 19h16M4 15l4-8 4 5 4-7 4 10" />
        <path d="M4 19V5" />
      </>
    ),
    deliverables: (
      <>
        <rect x="3" y="4" width="18" height="16" rx="2" />
        <path d="M7 9h10M7 13h7M7 17h5" />
      </>
    ),
  };

  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      {icons[type] || icons.objectives}
    </svg>
  );
};

const IconDownload = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" />
  </svg>
);

const SupportIcon = ({ type }) => {
  const paths = {
    incubation: <><path d="M12 2l7 3v6c0 5-3.5 9-7 11-3.5-2-7-6-7-11V5l7-3z" /></>,
    students: <><path d="M22 10v6M2 10l10-5 10 5-10 5z" /><path d="M6 12v5c0 1.7 2.7 3 6 3s6-1.3 6-3v-5" /></>,
    faculty: <><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" /></>,
    mba: <><path d="M4 19.5A2.5 2.5 0 016.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z" /></>,
    networking: <><circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" /><path d="M8.59 13.51l6.83 3.98M15.41 6.51l-6.82 3.98" /></>,
  };

  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      {paths[type] || paths.incubation}
    </svg>
  );
};

const NISPPolicy = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activePhase, setActivePhase] = useState("objectives");
  const [activeThrustGroup, setActiveThrustGroup] = useState("");
  const [activeSupport, setActiveSupport] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "NISP Policy | NCET";
  }, []);

  // Override the bundled fallback with backend content (singleton).
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await getNispPolicy();
        if (active) setData(res?.data || null);
      } catch (e) {
        if (active) setData(null);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const hero = data?.hero || {};
  const stats = data?.stats || [];
  const foundation = data?.foundation || {};
  const vision = foundation.vision || {};
  const mission = foundation.mission || {};
  const thrust = data?.thrust || {};
  const thrustGroups = thrust.groups || [];
  const framework = data?.framework || {};
  const objectives = framework.objectives || {};
  const goals = framework.goals || {};
  const deliverablesPhase = framework.deliverables || {};
  const support = data?.support || {};
  const supportPillars = support.pillars || [];
  const cta = data?.cta || {};

  // Derived phase list (counts computed from content).
  const phases = [
    { id: "objectives", label: objectives.label, step: objectives.step, summary: objectives.summary, count: (objectives.items || []).length },
    { id: "goals", label: goals.label, step: goals.step, summary: goals.summary, count: (goals.shortTerm || []).length + (goals.longTerm || []).length },
    { id: "deliverables", label: deliverablesPhase.label, step: deliverablesPhase.step, summary: deliverablesPhase.summary, count: (deliverablesPhase.deliverables || []).length + (deliverablesPhase.promotion || []).length },
  ];

  const supportTotal = supportPillars.reduce((sum, pillar) => sum + (pillar.items || []).length, 0);

  // Keep the active selections valid as data loads/changes.
  useEffect(() => {
    if (thrustGroups.length && !thrustGroups.some((g) => g.id === activeThrustGroup)) {
      setActiveThrustGroup(thrustGroups[0].id);
    }
  }, [thrustGroups, activeThrustGroup]);
  useEffect(() => {
    if (supportPillars.length && !supportPillars.some((p) => p.id === activeSupport)) {
      setActiveSupport(supportPillars[0].id);
    }
  }, [supportPillars, activeSupport]);

  const activeGroupIndex = Math.max(0, thrustGroups.findIndex((group) => group.id === activeThrustGroup));
  const activeGroup = thrustGroups[activeGroupIndex] || { areas: [] };
  // Global thrust-area number offset = total areas of all preceding groups.
  const activeGroupOffset = thrustGroups
    .slice(0, activeGroupIndex)
    .reduce((sum, g) => sum + (g.areas || []).length, 0);

  const activePillarIndex = Math.max(0, supportPillars.findIndex((pillar) => pillar.id === activeSupport));
  const activePillar = supportPillars[activePillarIndex] || { items: [] };

  if (loading || !data) {
    return (
      <div className="nsp-page">
        <Header />
        <PageBanner title="National Innovation & Startup Policy" />
        <div className="container" style={{ padding: "6rem 0", textAlign: "center", color: "#53545b" }}>
          {loading ? "Loading…" : "NISP policy content is currently unavailable. Please check back later."}
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="nsp-page">
      <Header />

      <PageBanner
        title={hero.title}
        eyebrow={hero.eyebrow}
        subtitle={hero.subtitle}
        image={hero.bannerImage ? mediaUrl(hero.bannerImage) : undefined}
      >
        <div className="nsp-banner-actions" style={{ display: 'flex', gap: '0.65rem', flexWrap: 'wrap', marginTop: '1.5rem' }}>
          {hero.downloadPdf && (
            <a href={mediaUrl(hero.downloadPdf)} target="_blank" rel="noopener noreferrer" className="nsp-hero__btn nsp-hero__btn--primary">
              <IconDownload />
              {hero.downloadLabel || "Download Full Policy"}
            </a>
          )}
          {hero.exploreLabel && (
            <a href="#nsp-framework" className="nsp-hero__btn nsp-hero__btn--ghost">
              {hero.exploreLabel}
            </a>
          )}
        </div>
      </PageBanner>

      <div className="container" style={{ marginTop: '2rem', position: 'relative', zIndex: 10, paddingBottom: '3rem' }}>
        <motion.div
          className="nsp-statband"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: EASE, delay: 0.12 }}
        >
          {stats.map((stat, index) => (
            <div className="nsp-statband__item" key={index}>
              <span className="nsp-statband__value">{stat.value}</span>
              <span className="nsp-statband__label">{stat.label}</span>
            </div>
          ))}
        </motion.div>
      </div>

      <section className="nsp-foundation">
        <div className="container">
          <div className="nsp-foundation__grid">
            <motion.article
              className="nsp-foundation-card"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, ease: EASE }}
            >
              <header className="nsp-foundation-card__head">
                <span className="nsp-block__tag">{vision.tag}</span>
                <h2 className="nsp-block__title">{vision.title}</h2>
                <p className="nsp-foundation-card__lede">{vision.lede}</p>
              </header>

              <ol className="nsp-foundation-track">
                {(vision.pillars || []).map((pillar, index) => (
                  <li
                    key={index}
                    className={`nsp-foundation-step ${pillar.featured ? "nsp-foundation-step--featured" : ""}`}
                  >
                    <span className="nsp-foundation-step__node" aria-hidden="true">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <div className="nsp-foundation-step__card">
                      <span className="nsp-foundation-step__label">{pillar.label}</span>
                      <p>{pillar.text}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </motion.article>

            <motion.article
              className="nsp-foundation-card"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, ease: EASE, delay: 0.08 }}
            >
              <header className="nsp-foundation-card__head">
                <span className="nsp-block__tag">{mission.tag}</span>
                <h2 className="nsp-block__title">{mission.title}</h2>
                <p className="nsp-foundation-card__lede">{mission.lede}</p>
              </header>

              <ol className="nsp-foundation-track">
                {(mission.pillars || []).map((pillar, index) => (
                  <li key={index} className="nsp-foundation-step">
                    <span className="nsp-foundation-step__node" aria-hidden="true">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <div className="nsp-foundation-step__card">
                      <span className="nsp-foundation-step__label">{pillar.label}</span>
                      <p>{pillar.text}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </motion.article>
          </div>
        </div>
      </section>

      <section className="nsp-thrust-hub" aria-labelledby="nsp-thrust-heading">
        <div className="container">
          <div className="nsp-thrust-hub__header">
            <div>
              <span className="nsp-thrust-hub__eyebrow">{thrust.eyebrow}</span>
              <h2 id="nsp-thrust-heading">{thrust.heading}</h2>
              <p className="nsp-thrust-hub__lede">{thrust.lede}</p>
            </div>
            <div className="nsp-thrust-hub__stat" aria-hidden="true">
              <strong>{thrust.statValue}</strong>
              <span>{thrust.statLabel}</span>
            </div>
          </div>

          <div className="nsp-thrust-hub__shell">
            <div className="nsp-thrust-hub__lanes" role="tablist" aria-label="Thrust area groups">
              {thrustGroups.map((group, index) => (
                <button
                  key={group.id || index}
                  type="button"
                  role="tab"
                  aria-selected={activeThrustGroup === group.id}
                  className={`nsp-thrust-lane ${activeThrustGroup === group.id ? "is-active" : ""}`}
                  onClick={() => setActiveThrustGroup(group.id)}
                >
                  <span className="nsp-thrust-lane__index">{String(index + 1).padStart(2, "0")}</span>
                  <span className="nsp-thrust-lane__label">{group.label}</span>
                  <span className="nsp-thrust-lane__range">{group.range}</span>
                </button>
              ))}
            </div>

            <AnimatePresence mode="wait">
              <motion.div
                key={activeThrustGroup}
                className="nsp-thrust-hub__track"
                role="tabpanel"
                initial={{ opacity: 0, x: 16 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -12 }}
                transition={{ duration: 0.3, ease: EASE }}
              >
                <div className="nsp-thrust-hub__track-head">
                  <span className="nsp-thrust-hub__track-tag">{activeGroup.label}</span>
                  <p>Areas {activeGroup.range} of the National Innovation and Startup Policy framework.</p>
                </div>

                <ol className="nsp-thrust-timeline">
                  {(activeGroup.areas || []).map((area, index) => (
                    <li key={index} className="nsp-thrust-timeline__item">
                      <span className="nsp-thrust-timeline__node" aria-hidden="true">
                        {String(activeGroupOffset + index + 1).padStart(2, "0")}
                      </span>
                      <div className="nsp-thrust-timeline__card">
                        <span className="nsp-thrust-timeline__step">Thrust {activeGroupOffset + index + 1}</span>
                        <p>{area}</p>
                      </div>
                    </li>
                  ))}
                </ol>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </section>

      <section className="nsp-ops-hub" id="nsp-framework" aria-labelledby="nsp-ops-heading">
        <div className="container">
          <div className="nsp-ops-hub__intro">
            <span className="nsp-ops-hub__eyebrow">{framework.eyebrow}</span>
            <h2 id="nsp-ops-heading">{framework.heading}</h2>
            <p>{framework.intro}</p>
          </div>

          <div className="nsp-ops-hub__layout">
            <nav className="nsp-ops-hub__nav" aria-label="Implementation phases">
              {phases.map((phase) => (
                <button
                  key={phase.id}
                  type="button"
                  className={`nsp-ops-phase ${activePhase === phase.id ? "is-active" : ""}`}
                  aria-current={activePhase === phase.id ? "step" : undefined}
                  onClick={() => setActivePhase(phase.id)}
                >
                  <span className="nsp-ops-phase__icon"><FrameworkIcon type={phase.id} /></span>
                  <span className="nsp-ops-phase__body">
                    <span className="nsp-ops-phase__step">Phase {phase.step}</span>
                    <span className="nsp-ops-phase__label">{phase.label}</span>
                    <span className="nsp-ops-phase__summary">{phase.summary}</span>
                  </span>
                  <span className="nsp-ops-phase__count">{phase.count}</span>
                </button>
              ))}
            </nav>

            <AnimatePresence mode="wait">
              <motion.div
                key={activePhase}
                className="nsp-ops-hub__stage"
                role="region"
                aria-label={phases.find((phase) => phase.id === activePhase)?.label}
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.28, ease: EASE }}
              >
                {activePhase === "objectives" && (
                  <div className="nsp-ops-objectives">
                    <header className="nsp-ops-stage__head">
                      <h3>{objectives.heading}</h3>
                      <p>{objectives.subheading}</p>
                    </header>
                    <ul className="nsp-ops-objectives__list">
                      {(objectives.items || []).map((objective, index) => (
                        <li key={index} className="nsp-ops-objective">
                          <span className="nsp-ops-objective__index">{String(index + 1).padStart(2, "0")}</span>
                          <span className="nsp-ops-objective__text">{objective}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {activePhase === "goals" && (
                  <div className="nsp-ops-goals">
                    <header className="nsp-ops-stage__head">
                      <h3>{goals.heading}</h3>
                      <p>{goals.subheading}</p>
                    </header>
                    <div className="nsp-ops-goals__lanes">
                      <article className="nsp-ops-goals__lane nsp-ops-goals__lane--short">
                        <div className="nsp-ops-goals__lane-head">
                          <span className="nsp-ops-goals__badge">{goals.shortLabel}</span>
                          <strong>{(goals.shortTerm || []).length} goals</strong>
                        </div>
                        <ol className="nsp-ops-goals__steps">
                          {(goals.shortTerm || []).map((goal, index) => (
                            <li key={index}>
                              <span className="nsp-ops-goals__dot" aria-hidden="true" />
                              <span className="nsp-ops-goals__num">{index + 1}</span>
                              <p>{goal}</p>
                            </li>
                          ))}
                        </ol>
                      </article>
                      <article className="nsp-ops-goals__lane nsp-ops-goals__lane--long">
                        <div className="nsp-ops-goals__lane-head">
                          <span className="nsp-ops-goals__badge">{goals.longLabel}</span>
                          <strong>{(goals.longTerm || []).length} goals</strong>
                        </div>
                        <ol className="nsp-ops-goals__steps">
                          {(goals.longTerm || []).map((goal, index) => (
                            <li key={index}>
                              <span className="nsp-ops-goals__dot" aria-hidden="true" />
                              <span className="nsp-ops-goals__num">{index + 1}</span>
                              <p>{goal}</p>
                            </li>
                          ))}
                        </ol>
                      </article>
                    </div>
                  </div>
                )}

                {activePhase === "deliverables" && (
                  <div className="nsp-ops-deliver">
                    <header className="nsp-ops-stage__head">
                      <h3>{deliverablesPhase.heading}</h3>
                      <p>{deliverablesPhase.subheading}</p>
                    </header>
                    <div className="nsp-ops-deliver__grid">
                      <article className="nsp-ops-deliver__panel">
                        <div className="nsp-ops-deliver__panel-head">
                          <span className="nsp-ops-deliver__tag">{deliverablesPhase.deliverablesLabel}</span>
                          <span className="nsp-ops-deliver__count">{(deliverablesPhase.deliverables || []).length} items</span>
                        </div>
                        <ul className="nsp-ops-deliver__items">
                          {(deliverablesPhase.deliverables || []).map((item, index) => (
                            <li key={index}>
                              <span className="nsp-ops-deliver__index">{String(index + 1).padStart(2, "0")}</span>
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </article>
                      <article className="nsp-ops-deliver__panel nsp-ops-deliver__panel--promo">
                        <div className="nsp-ops-deliver__panel-head">
                          <span className="nsp-ops-deliver__tag">{deliverablesPhase.promotionLabel}</span>
                          <span className="nsp-ops-deliver__count">{(deliverablesPhase.promotion || []).length} initiatives</span>
                        </div>
                        <ul className="nsp-ops-deliver__items">
                          {(deliverablesPhase.promotion || []).map((item, index) => (
                            <li key={index}>
                              <span className="nsp-ops-deliver__index">{String(index + 1).padStart(2, "0")}</span>
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </article>
                    </div>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </section>

      <section className="nsp-incub-hub" id="nsp-support" aria-labelledby="nsp-incub-heading">
        <div className="container">
          <div className="nsp-incub-hub__banner">
            <div className="nsp-incub-hub__banner-copy">
              <span className="nsp-incub-hub__eyebrow">{support.eyebrow}</span>
              <h2 id="nsp-incub-heading">{support.heading}</h2>
              <p>{support.intro}</p>
            </div>
            <div className="nsp-incub-hub__metrics" aria-label="Support overview">
              <div className="nsp-incub-hub__metric">
                <strong>{supportPillars.length}</strong>
                <span>Support Pillars</span>
              </div>
              <div className="nsp-incub-hub__metric">
                <strong>{supportTotal}</strong>
                <span>Total Initiatives</span>
              </div>
            </div>
          </div>

          <div className="nsp-incub-hub__shell">
            <div className="nsp-incub-hub__rail" role="tablist" aria-label="Support pillars">
              {supportPillars.map((pillar) => (
                <button
                  key={pillar.id}
                  type="button"
                  role="tab"
                  aria-selected={activeSupport === pillar.id}
                  className={`nsp-incub-pillar ${activeSupport === pillar.id ? "is-active" : ""}`}
                  onClick={() => setActiveSupport(pillar.id)}
                >
                  <span className="nsp-incub-pillar__icon"><SupportIcon type={pillar.id} /></span>
                  <span className="nsp-incub-pillar__label">{pillar.title}</span>
                  <span className="nsp-incub-pillar__count">{(pillar.items || []).length}</span>
                </button>
              ))}
            </div>

            <AnimatePresence mode="wait">
              <motion.article
                key={activeSupport}
                className="nsp-incub-hub__detail"
                role="tabpanel"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.28, ease: EASE }}
              >
                <header className="nsp-incub-detail__head">
                  <div className="nsp-incub-detail__title-wrap">
                    <span className="nsp-incub-detail__badge">Pillar {String(activePillarIndex + 1).padStart(2, "0")}</span>
                    <h3>{activePillar.title}</h3>
                    <p>{activePillar.tagline}</p>
                  </div>
                  <div className="nsp-incub-detail__icon" aria-hidden="true">
                    <SupportIcon type={activePillar.id} />
                  </div>
                </header>

                <ul className="nsp-incub-detail__grid">
                  {(activePillar.items || []).map((item, index) => (
                    <li key={index} className="nsp-incub-initiative">
                      <span className="nsp-incub-initiative__index">{String(index + 1).padStart(2, "0")}</span>
                      <p>{item}</p>
                    </li>
                  ))}
                </ul>
              </motion.article>
            </AnimatePresence>
          </div>
        </div>
      </section>

      <section className="nsp-cta">
        <div className="container">
          <div className="nsp-cta__card">
            <div>
              <span className="nsp-cta__eyebrow">{cta.eyebrow}</span>
              <h3>{cta.heading}</h3>
              <p>{cta.text}</p>
            </div>
            {cta.buttonPdf && (
              <a href={mediaUrl(cta.buttonPdf)} target="_blank" rel="noopener noreferrer" className="nsp-cta__btn">
                <IconDownload />
                {cta.buttonLabel || "Download Full Policy"}
              </a>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default NISPPolicy;
